import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class SharedService {
    redirectUrl: BehaviorSubject<any>;

    constructor() {
        this.redirectUrl = new BehaviorSubject<any>(null);
    }

    getRedirectUrl() {
        return this.redirectUrl.asObservable();
    }

    /**
     * Extracts and validates a redirect URL from query parameters to prevent open redirect attacks.
     *
     * The redirect parameter is expected to be base64-encoded for safe URL transmission.
     * This method decodes it and validates that the destination is a trusted sandbox.co.in domain.
     *
     * @param encoded - The base64-encoded redirect parameter value from query params
     * @returns An object containing:
     *   - `encoded`: The validated base64-encoded redirect parameter (used to pass to /login if auth fails; undefined if validation fails or param is missing)
     *   - `decoded`: The validated, decoded URL string (null if validation fails or param is missing)
     *
     * @example
     * // URL: /authorize?redirect=aHR0cHM6Ly9jb25zb2xlLnNhbmRib3guY28uaW4=
     * // Returns: { encoded: "aHR0cHM6Ly9jb25zb2xlLnNhbmRib3guY28uaW4=", decoded: "https://console.sandbox.co.in" }
     *
     * @example
     * // URL: /authorize?redirect=aHR0cHM6Ly9ldmlsLmNvbS9waGlzaA==  (evil.com)
     * // Returns: { encoded: undefined, decoded: null }  // Blocked!
     *
     * @example
     * // URL: /authorize  (no redirect param)
     * // Returns: { encoded: undefined, decoded: null }
     *
     * Security: Only allows redirects to *.sandbox.co.in domains to prevent phishing attacks
     * where an attacker could redirect users (with fresh tokens) to malicious sites.
     */
    static decodeAndValidateRedirectUrl(encoded: string | null | undefined): { encoded?: string; decoded: string | null } {
        if (!encoded || encoded === 'undefined') {
            return { encoded: undefined, decoded: null };
        }
        try {
            const decoded = atob(encoded);
            const url = new URL(decoded);
            if (!url.hostname.endsWith('.sandbox.co.in')) {
                return { encoded: undefined, decoded: null };
            }
            return { encoded: btoa(decoded), decoded };
        } catch {
            return { encoded: undefined, decoded: null };
        }
    }

    setRedirectUrl(encoded: string) {
        const { encoded: safe } = SharedService.decodeAndValidateRedirectUrl(encoded);
        if (safe) {
            this.redirectUrl.next(safe);
        }
    }
}


